// "Who is this site for?" — the first thing the CREATE wizard asks when it was
// launched from a BROKER, rendered in place of the identity fields until it is
// answered.
//
// WHY IT EXISTS. A broker under a firm can be given either the firm's shared
// site or a private one of their own, and the difference is not cosmetic: a
// broker's own site is PRIVATE FROM THEIR FIRM in the portal. Without being
// asked, a staff member creating what should have been an office-wide site
// silently produces one person's private site instead, and nobody at that firm
// ever sees it. So the consequence is stated in the option text itself, not in a
// footnote, and the shared option is the default.
//
// A brokerage launch and the standalone ownerless path never reach this — there
// is no second party for the choice to be between.
//
// AMBIGUOUS AFFILIATION. A broker multi-homed across active firms with no
// primary has no single covering firm, so the fork above cannot be asked yet.
// Rather than dead-ending, the staff member PICKS one of the candidate firms —
// which resolves the ambiguity and drops straight into the ordinary fork
// (firm-no-site or firm-has-site, depending on whether that firm runs a site).
// It is not a third terminal branch. When the server names no candidates the
// original advisory copy is still what renders.
//
// STAFF-ONLY, like everything else that touches site configuration: this renders
// behind Partners.ManageBrands.

import { Building2, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { Label } from "@/design-system/components/ui";
import type { PartnerBrandLaunchDecision } from "@/hooks/partners/usePartnerBrandLaunchDecision";

/** The ambiguity answer: a candidate brokerage id, or "no firm — personal
 *  site". Kept distinct from PartnerBrandContextChoice, which is the SECOND
 *  question (shared vs personal) and only makes sense once a firm is known. */
export const AMBIGUOUS_PERSONAL = "personal-only";

/** shared-firm: the firm's site. personal: this broker's own, private from the
 *  firm. use-existing: create nothing — keep inheriting the firm's site. */
export type PartnerBrandContextChoice = "shared-firm" | "personal" | "use-existing";

interface Option {
  value: PartnerBrandContextChoice;
  label: string;
  consequence: string;
}

function optionsFor(decision: PartnerBrandLaunchDecision): Option[] {
  if (decision.mode === "firm-no-site") {
    return [
      {
        value: "shared-firm",
        label: `Create a shared site for ${decision.firm.name}`,
        consequence:
          `Every broker at ${decision.firm.name} without a site of their own uses it, and ` +
          `${decision.firm.name} sees every completion that comes through it.`,
      },
      {
        value: "personal",
        label: `Create a personal site for ${decision.brokerName} only`,
        consequence:
          `Only ${decision.brokerName} uses it, and it is private from ${decision.firm.name} — ` +
          `nobody else at the firm sees the site or its completions in their portal.`,
      },
    ];
  }
  if (decision.mode === "firm-has-site") {
    return [
      {
        value: "use-existing",
        label: `Keep using ${decision.firm.name}'s existing site`,
        consequence:
          `Nothing is created. ${decision.brokerName} keeps inheriting ` +
          `${decision.site.displayName}, and their completions stay visible to ` +
          `${decision.firm.name}. We record that you reviewed this.`,
      },
      {
        value: "personal",
        label: `Create a personal site for ${decision.brokerName} only`,
        consequence:
          `Replaces the inherited site for ${decision.brokerName} alone, and it is private ` +
          `from ${decision.firm.name} — nobody else at the firm sees the site or its ` +
          `completions in their portal. Colors, fonts, hero copy and contact details are ` +
          `copied from ${decision.site.displayName} as a starting point; the name, slug and ` +
          `reference code are new.`,
      },
    ];
  }
  return [];
}

/** The default is the SHARED outcome in both forks — the safer mistake is an
 *  office-wide site the firm can see, not a private one nobody knows exists. */
export function defaultChoiceFor(
  decision: PartnerBrandLaunchDecision,
): PartnerBrandContextChoice {
  return optionsFor(decision)[0]?.value ?? "personal";
}

interface Props {
  decision: PartnerBrandLaunchDecision;
  value: PartnerBrandContextChoice;
  onChange: (next: PartnerBrandContextChoice) => void;
  /** Ambiguous affiliation only — the picked candidate firm id, or
   *  AMBIGUOUS_PERSONAL. Null until the staff member picks. */
  firmChoice?: string | null;
  onFirmChoiceChange?: (next: string) => void;
}

/** Pick which of a multi-homed broker's firms the site belongs to. Answering
 *  hands back to the ordinary fork above — nothing is decided here except
 *  WHICH firm the shared/personal question is about. */
function AmbiguousFirmPicker({
  decision,
  firmChoice,
  onFirmChoiceChange,
}: {
  decision: Extract<PartnerBrandLaunchDecision, { mode: "ambiguous-firm" }>;
  firmChoice: string | null;
  onFirmChoiceChange: (next: string) => void;
}) {
  return (
    <section
      className="flex flex-col gap-space-3"
      data-testid="partner-brand-context-ambiguous-picker"
    >
      <h2 className="font-display font-bold text-h3 m-0">Which firm is this site for?</h2>
      <p className="text-small text-gray-mid m-0 max-w-reading">
        {decision.brokerName} is affiliated with more than one active brokerage and none is
        marked primary, so there is no single firm a shared site would belong to. Choose the
        firm this site is for — you will still be asked whether it is that firm&rsquo;s shared
        site or {decision.brokerName}&rsquo;s own.
      </p>

      <fieldset className="flex flex-col gap-space-2 m-0 p-0 border-0">
        <legend className="sr-only">Candidate brokerage</legend>
        {decision.candidates.map((firm) => (
          <label
            key={firm.id}
            className="flex items-start gap-space-2 rounded-md border border-border-light bg-white-pure p-space-3 text-small cursor-pointer"
          >
            <input
              type="radio"
              name="partner-brand-ambiguous-firm"
              className="mt-space-1"
              data-testid={`context-firm-${firm.id}`}
              value={firm.id}
              checked={firmChoice === firm.id}
              onChange={() => onFirmChoiceChange(firm.id)}
            />
            <span className="min-w-0">
              <span className="block font-semibold text-onyx">{firm.name}</span>
            </span>
          </label>
        ))}
        <label className="flex items-start gap-space-2 rounded-md border border-border-light bg-white-pure p-space-3 text-small cursor-pointer">
          <input
            type="radio"
            name="partner-brand-ambiguous-firm"
            className="mt-space-1"
            data-testid={`context-firm-${AMBIGUOUS_PERSONAL}`}
            value={AMBIGUOUS_PERSONAL}
            checked={firmChoice === AMBIGUOUS_PERSONAL}
            onChange={() => onFirmChoiceChange(AMBIGUOUS_PERSONAL)}
          />
          <span className="min-w-0">
            <span className="block font-semibold text-onyx">
              None of them — a personal site for {decision.brokerName} only
            </span>
            <span className="block text-caption text-gray-mid mt-space-1">
              Private from every one of those firms — none of them sees the site or its
              completions in their portal.
            </span>
          </span>
        </label>
      </fieldset>

      <p className="text-caption text-gray-mid m-0 max-w-reading">
        Picking a firm here does not change {decision.brokerName}&rsquo;s affiliations. To make
        one of them the firm that resolves everywhere, set a primary affiliation on the
        broker&rsquo;s record.
      </p>
    </section>
  );
}

export function PartnerBrandContextDecision({
  decision,
  value,
  onChange,
  firmChoice = null,
  onFirmChoiceChange,
}: Props) {
  if (decision.mode === "ambiguous-firm") {
    if (decision.candidates.length === 0 || !onFirmChoiceChange) return null;
    return (
      <AmbiguousFirmPicker
        decision={decision}
        firmChoice={firmChoice}
        onFirmChoiceChange={onFirmChoiceChange}
      />
    );
  }

  const options = optionsFor(decision);
  if (options.length === 0) return null;

  const intro =
    decision.mode === "firm-no-site"
      ? `${decision.brokerName} is affiliated with ${decision.firm.name}, which has no prequalification site yet.`
      : decision.mode === "firm-has-site"
        ? `${decision.brokerName} currently inherits ${decision.firm.name}'s prequalification site, ${decision.site.displayName} (/prequalify/${decision.site.slug}).`
        : "";

  return (
    <section className="flex flex-col gap-space-3" data-testid="partner-brand-context-decision">
      <h2 className="font-display font-bold text-h3 m-0">Who is this site for?</h2>
      <p className="text-small text-gray-mid m-0 max-w-reading">{intro}</p>

      <fieldset className="flex flex-col gap-space-2 m-0 p-0 border-0">
        <legend className="sr-only">Site scope</legend>
        {options.map((opt) => (
          <label
            key={opt.value}
            className="flex items-start gap-space-2 rounded-md border border-border-light bg-white-pure p-space-3 text-small cursor-pointer"
          >
            <input
              type="radio"
              name="partner-brand-context-choice"
              className="mt-space-1"
              data-testid={`context-choice-${opt.value}`}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
            />
            <span className="min-w-0">
              <span className="block font-semibold text-onyx">{opt.label}</span>
              <span className="block text-caption text-gray-mid mt-space-1">
                {opt.consequence}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
    </section>
  );
}

/** The advisory states — a notice, not a fork, because in each of them there is
 *  only one thing the wizard can actually do. They are named rather than left to
 *  read as "this broker just has no site". */
export function PartnerBrandContextNotice({
  decision,
}: {
  decision: PartnerBrandLaunchDecision;
}) {
  if (decision.mode === "ambiguous-firm") {
    return (
      <div
        className="flex items-start gap-space-2 rounded-lg border border-border-light bg-white-warm p-space-3 text-small"
        data-testid="partner-brand-context-ambiguous"
      >
        <TriangleAlert size={14} strokeWidth={1.5} aria-hidden className="mt-space-1 shrink-0" />
        <span>
          {decision.brokerName} is affiliated with more than one active brokerage and none is
          marked primary, so there is no single firm a shared site could belong to. Only a
          personal site for {decision.brokerName} can be created here — it will be private from
          every one of those firms. To create a firm site instead, set a primary affiliation on
          the broker&rsquo;s record, or start from that brokerage&rsquo;s own page.
        </span>
      </div>
    );
  }

  if (decision.mode === "owns-site") {
    return (
      <div
        className="flex items-start gap-space-2 rounded-lg border border-border-light bg-white-warm p-space-3 text-small"
        data-testid="partner-brand-context-owns-site"
      >
        <Building2 size={14} strokeWidth={1.5} aria-hidden className="mt-space-1 shrink-0" />
        <span>
          {decision.brokerName} already owns the prequalification site{" "}
          <Link
            to={`/partners/brands/${decision.site.brandId}`}
            className="text-gold-shadow hover:underline"
          >
            {decision.site.displayName}
          </Link>
          . An owner can have only one site, so creating a second one for them will be refused —
          edit that site, or change the owner below.
        </span>
      </div>
    );
  }

  if (decision.mode === "unavailable") {
    return (
      <p className="text-caption text-gray-mid m-0" data-testid="partner-brand-context-unavailable">
        Could not check whether this broker sits under a brokerage. You can still create a
        personal site for them; to create a firm site, start from that brokerage&rsquo;s own page.
      </p>
    );
  }

  return null;
}

/** Shown while the decision is still resolving, in place of the identity fields:
 *  creating a personal site before the shared option was ever offered is the
 *  exact mistake this step exists to prevent. */
export function PartnerBrandContextLoading() {
  return (
    <section className="flex flex-col gap-space-2" data-testid="partner-brand-context-loading">
      <Label>Checking this broker&rsquo;s brokerage…</Label>
      <p className="text-caption text-gray-mid m-0">
        A broker under a firm can be given the firm&rsquo;s shared site instead of their own.
      </p>
    </section>
  );
}
